// HowItWorksSection.jsx
import React, { useEffect } from "react";

export default function HowItWorksSection() {
  useEffect(() => {
    const reveals = document.querySelectorAll(".flow-reveal");
    const nums = document.querySelectorAll(".step-num");

    const onScroll = () => {
      reveals.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 120) {
          el.classList.add("active");
        }
      });

      nums.forEach((num) => {
        const rect = num.getBoundingClientRect();
        if (rect.top < window.innerHeight - 120) {
          num.classList.add("active");
        }
      });
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const steps = [
    {
      n: "1",
      title: "Book a Free Discovery Call",
      desc: "We discuss your goals, ideas, workflow, tech needs, timeline, and budget. Clear, friendly, non-salesy.",
      svg: (
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#785f14]">
          <path fill="currentColor" d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011-.24 11.36 11.36 0 003.56.57 1 1 0 011 1v3.61a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.61a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 1 0 01-.24 1z"/>
        </svg>
      ),
    },
    {
      n: "2",
      title: "Project Plan & Proposal",
      desc: "You receive a crystal-clear plan, exact features, pricing, timelines, and milestones. No hidden surprises.",
      svg: (
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#785f14]">
          <path fill="currentColor" d="M19 3H5a2 2 0 00-2 2v14l4-4h12a2 2 0 002-2V5a2 2 0 00-2-2z"/>
        </svg>
      ),
    },
    {
      n: "3",
      title: "Development & Previews",
      desc: "You see your product come alive — weekly updates, staging links, UI previews, and full transparency.",
      svg: (
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#785f14]">
          <path fill="currentColor" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
        </svg>
      ),
    },
    {
      n: "4",
      title: "Launch & Long-term Support",
      desc: "Your product goes live smoothly — with optional maintenance, hosting, analytics, and future expansions.",
      svg: (
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#785f14]">
          <path fill="currentColor" d="M2 21h20L12 2 2 21z"/>
        </svg>
      ),
    },
  ];

  return (
    <section className="py-20 px-4">
      <div className="max-w-5xl mx-auto">

        <h2 className="text-center text-xl sm:text-2xl font-copperplate text-[#785f14] flow-reveal">
          <span className="font-semibold">How Your Project Comes to Life</span>
        </h2>

        <p className="text-center text-gray-600 mt-2 max-w-xl mx-auto flow-reveal delay-1">
          A premium, engineered process that ensures clarity, speed, and zero guesswork.
        </p>

        {/* DESKTOP — HORIZONTAL FLOW */}
        <div className="hidden lg:flex justify-between mt-16 relative">

          {/* Connecting line */}
          <div className="absolute top-10 left-0 right-0 h-[3px] bg-[#785f14]/40"></div>

          {steps.map((step, i) => (
            <div
              key={i}
              className={`flow-reveal delay-${i + 1} w-1/4 flex flex-col items-center text-center px-4 relative`}
            >
              {/* Number behind icon */}
              <div className={`step-num absolute -top-14 text-7xl font-bold text-[#785f14] opacity-0 pointer-events-none delay-${i + 1}`}>
                {step.n}
              </div>

              {/* Icon circle */}
              <div className="
                w-16 h-16 rounded-full 
                bg-white/60 backdrop-blur-md 
                border border-[#785f14]/40
                flex items-center justify-center
                shadow-md 
                hover:shadow-xl hover:scale-110
                transition
                text-[#785f14]
              ">
                {step.svg}
              </div>

              <h3 className="mt-6 font-semibold text-[#785f14] text-lg">{step.title}</h3>
              <p className="text-gray-600 text-sm mt-2">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* MOBILE — VERTICAL TIMELINE */}
        <div className="lg:hidden mt-16 relative">
          {steps.map((step, i) => (
            <div key={i} className="relative pl-10 mb-14 flow-reveal delay-1">

              {/* Number behind circle */}
              <div className={`step-num absolute -left-4 -top-6 text-5xl font-bold text-[#785f14] opacity-0 pointer-events-none delay-${i + 1}`}>
                {step.n}
              </div>

              {/* Timeline line */}
              {i < steps.length - 1 && (
                <div className="absolute top-10 left-4 w-[2px] h-full bg-[#785f14]/40"></div>
              )}

              {/* Circle */}
              <div className="
                absolute left-0 top-0 w-8 h-8 rounded-full 
                bg-white/80 backdrop-blur-md 
                border border-[#785f14]/40 
                flex items-center justify-center 
                shadow-sm text-[#785f14]
              ">
                {step.svg}
              </div>

              {/* Card */}
              <div className="
                bg-white/70 backdrop-blur-lg 
                border border-gray-200/50
                p-4 rounded-xl shadow-sm 
                hover:shadow-lg transition
              ">
                <h3 className="font-semibold text-[#785f14]">{step.title}</h3>
                <p className="text-gray-600 text-sm mt-1">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
